// A carousel's autoplay (the 2026 sidebar's Highlights; Seán's specs, 2026-10-22). Requested (the
// caller's, from the address: &autoplay=1|0) is apart from running, which also needs the carousel
// on screen, the page visible, no mouse over it and nothing holding it (e.g. a dialog open): those
// hold it a while, the request unchanged, each end starting afresh. Endless: after the last, the
// first. Stopped for good (onStop: the request off) by the button, a deliberate choice (the
// caller's) and keyboard focus entering the carousel (not its own button; the W3C carousel
// pattern). One timer, through two phases: "filling" (the button's countdown fill, as long as
// the wait), then at the step "retracting" (the fill drawn back as the next slides in), then
// filling again; "idle" when not running. `cycle` counts the fills, so each starts afresh.
import {onUnmounted, ref, watch} from "vue";
import {useDocumentVisibility, useElementVisibility} from "@vueuse/core";

export const AUTOPLAY_DELAY = 15000; // ms between highlights
export const RETRACT_TIME = 400; // ms: the fill drawn back, as the slide changes

// slug: {current(), next()}, the highlight shown and the one after (after the last, the first);
// onStart, onStep(next), onStop: the caller's (the address: on, a step, off)
export default function useAutoplay(options) {
  const {isRequested, slug, target, isHeldBy, onStart, onStep, onStop} = options;
  const isHovered = ref(false);
  const isPageShown = useDocumentVisibility();
  const isOnScreen = useElementVisibility(target);
  const isRunning = ref(false);
  const phase = ref("idle");
  const cycle = ref(0);
  let timer = 0;
  const getIsRunning = () => isRequested.value && !isHovered.value && !isHeldBy.value
    && isPageShown.value === "visible" && isOnScreen.value;
  function fill() {
    phase.value = "filling";
    cycle.value++;
    timer = setTimeout(stepAndRetract, AUTOPLAY_DELAY);
  }
  function stepAndRetract() {
    onStep(slug.next());
    phase.value = "retracting";
    timer = setTimeout(fill, RETRACT_TIME);
  }
  // any change of running: the timer cleared; running, a fresh fill; not, none (at once)
  function restart() {
    clearTimeout(timer);
    isRunning.value = getIsRunning();
    if (isRunning.value) return fill();
    phase.value = "idle";
  }
  watch([isRequested, isHovered, isHeldBy, isPageShown, isOnScreen], restart, {immediate: true});
  onUnmounted(() => clearTimeout(timer));
  // the carousel's own events: a mouse over it (not a touch: a tap would leave it "hovered"),
  // keyboard focus entering it (not its button)
  const events = {
    pointerenter: (event) => (isHovered.value = event.pointerType === "mouse"),
    pointerleave: () => (isHovered.value = false),
    focusin: (event) => {
      if (event.target.closest(".autoplay") || !event.target.matches(":focus-visible")) return;
      if (isRequested.value) onStop();
    },
  };
  const toggle = () => (isRequested.value ? onStop() : onStart());
  return {isRunning, phase, cycle, events, toggle};
}
